import React, { useEffect, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import { makeStyles } from '@material-ui/core/styles'
import { useId } from 'hooks'
import Button from '../Button/Button'
import CommonInput from '../CommonInput/CommonInput'
import CommonInputType from '../../helpers/types/CommonInput.interface'
import SimpleColorType from '../../helpers/types/SimpleColor.interface'
import RangeCalendar from './RangeCalendar'
import { addMonths, copyRange, DateRange, DateRangePreset, defaultPresets, isRangeAllowed, startOfDay } from './dateRange'
import style from './style'

/**
 * Props for the controlled Date range field.
 */
export interface DateRangePickerType extends Omit<CommonInputType, 'value' | 'onClear' | 'maxLength'> {
    value: DateRange | null
    onChange: (value: DateRange | null) => void
    label?: string
    placeholder?: string
    name?: string
    required?: boolean
    color?: SimpleColorType
    presets?: DateRangePreset[]
    minDate?: Date
    maxDate?: Date
    locale?: string
    formatDate?: (date: Date) => string
    'aria-label'?: string
}

const useStyles = makeStyles(style)
const monthOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
const sameRange = (first: DateRange | null, second: DateRange | null) => !!first && !!second &&
    (first.from ? startOfDay(first.from).getTime() : null) ===
        (second.from ? startOfDay(second.from).getTime() : null) &&
    (first.to ? startOfDay(first.to).getTime() : null) ===
        (second.to ? startOfDay(second.to).getTime() : null)

/**
 * A foundations input that edits an inclusive Date range in a modal.
 */
const DateRangePicker = ({
    value,
    onChange,
    color = 'neutral',
    presets = defaultPresets,
    minDate,
    maxDate,
    locale = 'en-US',
    formatDate,
    placeholder = 'Select date range',
    disabled = false,
    size = 'large',
    trailingIcon = 'calendar',
    id,
    name,
    required,
    'aria-label': ariaLabel,
    'data-testid': dataTestId = 'date-range-picker',
    className,
    ...inputProps
}: DateRangePickerType): JSX.Element => {
    const generatedId = useId('date-range-picker')
    const fieldId = id || generatedId
    const classes = useStyles({ color })
    const [isOpen, setIsOpen] = useState(false)
    const [draft, setDraft] = useState<DateRange | null>(null)
    const [fromMonth, setFromMonth] = useState(addMonths(monthOf(new Date()), -1))
    const [toMonth, setToMonth] = useState(monthOf(new Date()))
    const [activePreset, setActivePreset] = useState<number | null>(null)
    const [appliedSelection, setAppliedSelection] = useState<{
        label: string | null, range: DateRange | null
    } | null>(null)
    const offeredRanges = presets.map(preset => copyRange(preset.getRange(startOfDay(new Date()))))
    const hasAppliedSelection = appliedSelection && sameRange(value, appliedSelection.range)
    const appliedPresetIndex = hasAppliedSelection
        ? presets.findIndex(preset => preset.label === appliedSelection?.label)
        : offeredRanges.findIndex(range => sameRange(value, range))
    useEffect(() => {
        if (appliedSelection && !hasAppliedSelection) setAppliedSelection(null)
    }, [appliedSelection, hasAppliedSelection])
    useEffect(() => {
        if (disabled) setIsOpen(false)
    }, [disabled])
    const format = formatDate || ((date: Date) => date.toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric'
    }))
    const displayRange = (range: DateRange | null) => {
        if (range?.from && range?.to) return `${format(range.from)} – ${format(range.to)}`
        if (range?.from) return `From ${format(range.from)}`
        if (range?.to) return `To ${format(range.to)}`
        return ''
    }
    const showRange = (range: DateRange | null) => {
        setDraft(copyRange(range))
        const today = startOfDay(new Date())
        const initialDate = minDate && today < startOfDay(minDate)
            ? minDate
            : (maxDate && today > startOfDay(maxDate) ? maxDate : today)
        const firstMonth = monthOf(range?.from || range?.to || initialDate)
        const lastMonth = range?.from && !range?.to
            ? addMonths(firstMonth, 1)
            : monthOf(range?.to || initialDate)
        setFromMonth(firstMonth < lastMonth ? firstMonth : addMonths(lastMonth, -1))
        setToMonth(lastMonth)
    }
    const changeFromMonth = (month: Date) => {
        setFromMonth(month)
        if (month >= toMonth) setToMonth(addMonths(month, 1))
    }
    const changeToMonth = (month: Date) => {
        setToMonth(month)
        if (month <= fromMonth) setFromMonth(addMonths(month, -1))
    }
    const open = () => {
        if (disabled) return
        showRange(value)
        setActivePreset(appliedPresetIndex === -1 ? null : appliedPresetIndex)
        setIsOpen(true)
    }
    const selectDate = (endpoint: 'from' | 'to', date: Date) => {
        setActivePreset(null)
        if (!draft) {
            setDraft({ [endpoint]: date })
        } else if (endpoint === 'from') {
            setDraft({ ...draft, from: date, ...(draft.to && date > draft.to ? { to: date } : {}) })
        } else {
            setDraft({ ...draft, ...(draft.from && date < draft.from ? { from: date } : {}), to: date })
        }
    }
    const apply = () => {
        setAppliedSelection({
            label: activePreset === null ? null : presets[activePreset].label,
            range: copyRange(draft)
        })
        onChange(copyRange(draft))
        setIsOpen(false)
    }

    return <>
        <CommonInput
            {...inputProps}
            value={value}
            disabled={disabled}
            size={size}
            trailingIcon={trailingIcon}
            id={fieldId}
            clearable={false}
            onClickWrapper={open}
            className={`${classes.input} ${className || ''}`}
            data-testid={dataTestId}
        >
            <input
                id={fieldId}
                name={name}
                required={required}
                className='text-field input'
                value={isOpen && activePreset !== null
                    ? presets[activePreset]?.label
                    : (appliedPresetIndex === -1 ? displayRange(value) : presets[appliedPresetIndex].label)}
                readOnly
                disabled={disabled}
                placeholder={placeholder}
                aria-label={ariaLabel || inputProps.label || placeholder}
                aria-haspopup='dialog'
                aria-expanded={isOpen && !disabled}
                aria-controls={isOpen ? `${fieldId}-dialog` : undefined}
                data-testid={dataTestId}
                onKeyDown={event => {
                    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
                        event.preventDefault()
                        open()
                    }
                }}
            />
        </CommonInput>
        <Dialog
            open={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            maxWidth={false}
            classes={{ paper: classes.paper }}
            PaperProps={{
                id: `${fieldId}-dialog`,
                'aria-label': inputProps.label || 'Select date range'
            }}
        >
            <div className={classes.layout}>
                <aside className={classes.presets} aria-label='Preset date ranges'>
                    <button
                        type='button'
                        className={classes.preset}
                        aria-pressed={activePreset === null}
                        onClick={() => setActivePreset(null)}
                    >Customised</button>
                    {presets.map((preset, index) => <button
                        type='button'
                        key={`${preset.label}-${index}`}
                        className={classes.preset}
                        aria-pressed={activePreset === index}
                        disabled={!isRangeAllowed(offeredRanges[index], minDate, maxDate)}
                        onClick={() => {
                            showRange(offeredRanges[index])
                            setActivePreset(index)
                        }}
                    >{preset.label}</button>)}
                </aside>
                <div className={classes.main}>
                    <div className={classes.toolbar}>
                        <div className={classes.summary} aria-live='polite'>
                            {displayRange(draft) || placeholder}
                        </div>
                        <Button type='button' variant='text' color={color} onClick={() => {
                            setDraft(null)
                            setActivePreset(null)
                        }}>Clear filters</Button>
                        <div className={classes.actions}>
                            <Button type='button' variant='text' color={color} onClick={() => setIsOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                type='button'
                                color={color}
                                disabled={(required && !draft?.from && !draft?.to) ||
                                    !isRangeAllowed(draft, minDate, maxDate)}
                                onClick={apply}
                            >Apply</Button>
                        </div>
                    </div>
                    <div className={classes.calendars}>
                        <RangeCalendar
                            label='From'
                            month={fromMonth}
                            selected={draft?.from || null}
                            onMonthChange={changeFromMonth}
                            onSelect={date => selectDate('from', date)}
                            range={draft}
                            minDate={minDate}
                            maxDate={maxDate}
                            locale={locale}
                            classes={classes}
                        />
                        <RangeCalendar
                            label='To'
                            month={toMonth}
                            selected={draft?.to || null}
                            onMonthChange={changeToMonth}
                            onSelect={date => selectDate('to', date)}
                            range={draft}
                            minDate={minDate}
                            maxDate={maxDate}
                            locale={locale}
                            classes={classes}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    </>
}

export default DateRangePicker
